"use client";

import * as React from "react";
import { usePathname, useRouter } from "next/navigation";
import { useTranslations } from "next-intl";
import {
  ArrowLeft,
  ClipboardList,
  History,
  Loader2,
  Radio,
  Search,
  SearchX,
  PauseCircle,
  ShieldAlert,
  Users,
  UsersRound,
} from "lucide-react";

import {
  Command,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { cn } from "@/src/lib/utils";
import {
  fetchAssignmentParticipantsCount,
  fetchAssignmentStudents,
  fetchAssignmentsByIds,
  fetchAssignmentsByStatus,
  fetchGroupStudents,
  fetchAllGroups,
  fetchAllStudents,
  fetchRecentAssignments,
  fetchRunningProctoredAssignments,
  dropGlobalSearchCache,
  fetchPresence,
  forgetWarmup,
  dropLiveNowCache,
  fetchStudentAssignments,
  fetchStudentById,
  OFFLINE_STATUS,
  searchAssignments,
  searchGroups,
  searchStudents,
  searchUsers,
  type AssignmentHit,
  type GroupHit,
  type LiveStatus,
  resolveLiveExam,
  type LiveNow,
  type LiveNowEntry,
  type Page,
  type StudentHit,
  type UserHit,
} from "./globalSearchApi";
import { toast } from "sonner";
import { useGetUserQuery } from "@/api/auth/authPrivateApi";
import { useGetTrustScoresByAssignmentQuery } from "@/api/reports/reportApi";
import { skipToken } from "@reduxjs/toolkit/query";
import { acquireJanusSweep, dropLiveRoomsCache } from "./janusSweep";
import { useMediaCall } from "@/src/lib/media-call/MediaCallProvider";
import SearchPreview, { type PreviewSelection } from "./SearchPreview";
import { getLiveNow, subscribeLiveNow, dropLiveNowStore } from "./liveNowStore";
import { readList, writeList, dropListState } from "./listStateStore";
import { sweepAssignment } from "./janusSweep";
import {
  PresenceAvatar,
  presenceLabelKey,
  presenceLevel,
  LivePulse,
  type PresenceLevel,
} from "./PresenceAvatar";
import { ScoreCell } from "@/app/assignments/[id]/components/DataTable/ScoreCell";
import {
  fetchAttemptList,
  fetchAttemptViolations,
  type AttemptSummary,
  type ViolationSummary,
} from "./globalSearchApi";
import {
  buildSessionUrl,
  dropResolveCache,
  resolveByAttemptId,
  type SessionTarget,
} from "@/app/[value]/realtime/resolveSession";
import { lookupStudent } from "@/app/[value]/realtime/attemptIndex";

interface GlobalSearchDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  handoff?: { query: string; session: boolean } | null;
  onHandoffConsumed?: () => void;
}

interface RecentEntry {
  label: string;
  detail: string | null;
  url: string;
  photo?: string | null;
}

type BrowseMode =
  | { kind: "none" }
  | { kind: "live" }
  | { kind: "violations" }
  | { kind: "suspended" }
  | {
      kind: "exam";
      assignment: AssignmentHit;
      byViolations: boolean;
      from?: "none" | "violations" | "suspended";
    }
  | { kind: "group"; group: GroupHit }
  | { kind: "all-students" };

type SearchScope = "all" | "students" | "assignments" | "groups";

const SCOPES: Array<{ id: SearchScope; labelKey: string }> = [
  { id: "all", labelKey: "label-global-search-scope-all" },
  { id: "students", labelKey: "label-global-search-students" },
  { id: "assignments", labelKey: "label-global-search-assignments" },
  { id: "groups", labelKey: "label-global-search-groups" },
];

const RECENT_KEY = "te-global-search-recent";
const RECENT_LIMIT = 5;

interface OwnedRecent {
  owner: string;
  items: RecentEntry[];
}

const readRecent = (owner: string | null): RecentEntry[] => {
  if (typeof window === "undefined" || !owner) return [];
  try {
    const raw = window.localStorage.getItem(RECENT_KEY);
    const parsed = raw ? (JSON.parse(raw) as OwnedRecent) : null;

    if (!parsed || parsed.owner !== owner || !Array.isArray(parsed.items)) return [];

    return parsed.items.slice(0, RECENT_LIMIT);
  } catch {
    return [];
  }
};

const writeRecent = (owner: string | null, entry: RecentEntry) => {
  if (typeof window === "undefined" || !owner) return;
  try {
    const items = [entry, ...readRecent(owner).filter((item) => item.url !== entry.url)].slice(
      0,
      RECENT_LIMIT
    );

    window.localStorage.setItem(RECENT_KEY, JSON.stringify({ owner, items }));
  } catch {
    return;
  }
};

const isSessionNumber = (value: string) => /^\d{1,9}$/.test(value.trim());

const STATE_KEY = "te-global-search-state";

interface PersistedState {
  owner: string;
  query: string;
  scope: SearchScope;
  browse: BrowseMode;
  preview: PreviewSelection;
}

const readState = (owner: string | null): PersistedState | null => {
  if (typeof window === "undefined" || !owner) return null;
  try {
    const raw = window.sessionStorage.getItem(STATE_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw) as PersistedState | null;
    if (!parsed || typeof parsed !== "object" || parsed.owner !== owner) return null;

    return parsed;
  } catch {
    return null;
  }
};

const writeState = (state: PersistedState) => {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.setItem(STATE_KEY, JSON.stringify(state));
  } catch {
    return;
  }
};

const forgetScopedData = () => {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.removeItem(STATE_KEY);
    window.localStorage.removeItem(RECENT_KEY);
  } catch {
    return;
  }
};

const SESSION_LOOKUP_MIN_DIGITS = 5;
const STUDENT_CARD_FRESH_MS = 15 * 60 * 1000;
const LIVE_REFRESH_MS = 3_000;
const SCOPE_STALE_MS = 60_000;
const LIVE_PREVIEW_LIMIT = 5;
const ASSIGNMENT_BROWSE_KINDS = new Set(["violations", "suspended"]);
const PRESENCE_EXAM_LIMIT = 3;

type TrustScores = Record<number, number | null> | null;

function toTrustMap(list: Array<{ attempt_id: number; trust_score: number | null }> | undefined): TrustScores {
  if (!Array.isArray(list)) return null;

  const map: Record<number, number | null> = {};
  for (const item of list) map[item.attempt_id] = item.trust_score;

  return map;
}

function hasTrustScore(student: StudentHit, scores: TrustScores): boolean {
  if (!scores || student.attemptId == null) return false;
  return scores[student.attemptId] != null;
}

function resolveCredibility(student: StudentHit, scores: TrustScores): number {
  const trust = scores && student.attemptId != null ? scores[student.attemptId] : null;
  const resolved = trust ?? student.attemptCredibility ?? null;

  return resolved ?? -1;
}

function EMPTY_PAGE<T>(): Page<T> {
  return { items: [], hasMore: false };
}

const LIVE_PROMOTE_STEP_MS = 220;

function useStaggeredCounts(target: Record<number, number>): Record<number, number> {
  const [shown, setShown] = React.useState<Record<number, number>>({});

  React.useEffect(() => {
    const nextKey = Object.keys(target).find((key) => shown[Number(key)] !== target[Number(key)]);
    const goneKey =
      nextKey === undefined
        ? Object.keys(shown).find((key) => target[Number(key)] === undefined)
        : undefined;

    if (nextKey === undefined && goneKey === undefined) return;

    const timer = window.setTimeout(() => {
      setShown((previous) => {
        if (nextKey !== undefined) {
          return { ...previous, [Number(nextKey)]: target[Number(nextKey)] };
        }

        const next = { ...previous };
        delete next[Number(goneKey)];

        return next;
      });
    }, LIVE_PROMOTE_STEP_MS);

    return () => window.clearTimeout(timer);
  }, [target, shown]);

  return shown;
}


function takeFresh<T extends { id: number }>(items: T[], seen: Set<number>): T[] {
  const fresh = items.filter((item) => !seen.has(item.id));
  items.forEach((item) => seen.add(item.id));
  return fresh;
}

interface AssignmentSection {
  id: string;
  labelKey: string;
  items: AssignmentHit[];
}

function groupAssignments(
  assignments: AssignmentHit[],
  liveByExam: Record<number, number>
): AssignmentSection[] {
  const sections: AssignmentSection[] = [
    { id: "live", labelKey: "label-global-search-group-live", items: [] },
    { id: "open", labelKey: "label-global-search-group-open", items: [] },
    { id: "suspended", labelKey: "label-global-search-group-suspended", items: [] },
    { id: "planned", labelKey: "label-global-search-group-planned", items: [] },
    { id: "finished", labelKey: "label-global-search-group-finished", items: [] },
    { id: "draft", labelKey: "label-global-search-group-draft", items: [] },
  ];

  const byId = Object.fromEntries(sections.map((section) => [section.id, section]));

  for (const assignment of assignments) {
    if (liveByExam[assignment.id]) {
      byId.live.items.push(assignment);
      continue;
    }

    if (assignment.status === "process") {
      byId.open.items.push(assignment);
      continue;
    }

    if (assignment.status === "suspended") {
      byId.suspended.items.push(assignment);
      continue;
    }

    if (assignment.status === "remaining") {
      byId.planned.items.push(assignment);
      continue;
    }

    if (assignment.status === "draft") {
      byId.draft.items.push(assignment);
      continue;
    }

    byId.finished.items.push(assignment);
  }

  return sections.filter((section) => section.items.length > 0);
}

const LoadMore: React.FC<{ onReach: () => void; busy: boolean; loaded: number }> = ({
  onReach,
  busy,
  loaded,
}) => {
  const ref = React.useRef<HTMLDivElement>(null);
  const reach = React.useRef(onReach);

  React.useEffect(() => {
    reach.current = onReach;
  }, [onReach]);

  React.useEffect(() => {
    const node = ref.current;
    if (!node || busy) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) reach.current();
      },
      { root: node.closest("[cmdk-list]"), rootMargin: "160px" }
    );

    observer.observe(node);

    return () => observer.disconnect();
  }, [busy, loaded]);

  return (
    <div ref={ref} className="te-global-search__more flex items-center justify-center py-3">
      {busy && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
    </div>
  );
};

type SessionLookup =
  | { status: "idle" }
  | { status: "scanning" }
  | { status: "found"; target: SessionTarget; student: StudentHit | null }
  | { status: "not-found" };

const useAssignmentContext = (): number | null => {
  const pathname = usePathname();

  return React.useMemo(() => {
    const match = pathname?.match(/^\/assignments\/(\d+)/);
    return match ? Number(match[1]) : null;
  }, [pathname]);
};

const StudentAvatar: React.FC<{ student: StudentHit }> = ({ student }) => {
  const [failed, setFailed] = React.useState(false);

  if (student.photo && !failed) {
    return (
      <img
        src={student.photo}
        alt=""
        className="h-8 w-8 shrink-0 rounded-full object-cover"
        onError={() => setFailed(true)}
      />
    );
  }

  return (
    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-muted text-[11px] font-medium">
      {student.name
        .split(" ")
        .map((part) => part[0])
        .filter(Boolean)
        .slice(0, 2)
        .join("")
        .toUpperCase() || "?"}
    </span>
  );
};

const formatGroups = (groups: string[], extraLabel: (count: number) => string): string => {
  if (groups.length === 0) return "";
  if (groups.length <= 2) return groups.join(", ");
  return `${groups.slice(0, 2).join(", ")} ${extraLabel(groups.length - 2)}`;
};

export function GlobalSearchDialog({
  open,
  onOpenChange,
  handoff,
  onHandoffConsumed,
}: GlobalSearchDialogProps) {
  const t = useTranslations();

  const roleLabel = React.useCallback(
    (role: string): string => {
      const map: Record<string, string> = {
        student: "label-student",
        teacher: "label-teacher",
        reviewer: "label-reviewer",
        proctor: "label-global-search-role-proctor",
        manager: "label-global-search-role-manager",
        admin: "label-global-search-role-admin",
      };

      const key = map[role];
      return key ? t(key) : role;
    },
    [t]
  );
  const router = useRouter();
  const assignmentContextId = useAssignmentContext();

  const { data: me } = useGetUserQuery();
  const owner = me?.id != null ? `${me.school_id ?? 0}:${me.id}` : null;

  const [hydrated, setHydrated] = React.useState(false);
  const [query, setQuery] = React.useState("");
  const [submitted, setSubmitted] = React.useState("");
  const term = submitted.trim();
  const [isSearching, setIsSearching] = React.useState(false);
  const [contextStudents, setContextStudents] = React.useState<StudentHit[]>([]);
  const [students, setStudents] = React.useState<StudentHit[]>([]);
  const [assignments, setAssignments] = React.useState<AssignmentHit[]>([]);
  const [groups, setGroups] = React.useState<GroupHit[]>([]);
  const [users, setUsers] = React.useState<UserHit[]>([]);
  const [recent, setRecent] = React.useState<RecentEntry[]>([]);
  const [sessionLookup, setSessionLookup] = React.useState<SessionLookup>({ status: "idle" });
  const sessionCacheRef = React.useRef<Map<number, SessionLookup>>(new Map());

  const [scope, setScope] = React.useState<SearchScope>("all");
  const [scopeStudents, setScopeStudents] = React.useState<StudentHit[]>([]);
  const [scopeAssignments, setScopeAssignments] = React.useState<AssignmentHit[]>([]);
  const [scopeGroups, setScopeGroups] = React.useState<GroupHit[]>([]);
  const [isScopeLoading, setIsScopeLoading] = React.useState(false);
  const [scopePage, setScopePage] = React.useState(1);
  const [scopeHasMore, setScopeHasMore] = React.useState(false);
  const [participantCounts, setParticipantCounts] = React.useState<Record<number, number>>({});
  const [browse, setBrowse] = React.useState<BrowseMode>({ kind: "none" });
  const [browseAssignments, setBrowseAssignments] = React.useState<AssignmentHit[]>([]);
  const [browseStudents, setBrowseStudents] = React.useState<StudentHit[]>([]);
  const [isBrowseLoading, setIsBrowseLoading] = React.useState(false);
  const [browsePage, setBrowsePage] = React.useState(1);
  const [browseHasMore, setBrowseHasMore] = React.useState(false);
  const [isAppending, setIsAppending] = React.useState(false);
  const scopeSeenRef = React.useRef<Set<number>>(new Set());
  const browseSeenRef = React.useRef<Set<number>>(new Set());

  const [selectedStudent, setSelectedStudent] = React.useState<StudentHit | null>(null);
  const [preview, setPreview] = React.useState<PreviewSelection>(null);
  const [previewAssignments, setPreviewAssignments] = React.useState<AssignmentHit[]>([]);
  const [previewAttempts, setPreviewAttempts] = React.useState<AttemptSummary[]>([]);
  const [previewStudents, setPreviewStudents] = React.useState<StudentHit[]>([]);
  const [previewCount, setPreviewCount] = React.useState<number | null>(null);
  const [previewViolations, setPreviewViolations] = React.useState<ViolationSummary[]>([]);
  const [previewViolationsTotal, setPreviewViolationsTotal] = React.useState(0);
  const [previewLive, setPreviewLive] = React.useState<LiveStatus | null>(null);
  const [previewPresence, setPreviewPresence] = React.useState<Record<number, LiveStatus>>({});
  const [isPreviewLoading, setIsPreviewLoading] = React.useState(false);
  const { callUser } = useMediaCall();
  const [liveNow, setLiveNow] = React.useState<LiveNow | null>(null);
  const [isLiveLoading, setIsLiveLoading] = React.useState(false);


  const loadLiveNow = React.useCallback(
    async (force = false) => {
      if (!owner) return;
      if (force) {
        dropLiveNowCache();
        dropLiveNowStore();
      }

      setIsLiveLoading(true);

      const snapshot = await getLiveNow(owner, {
        force,
        onBackground: (fresh) => {
          setLiveNow(fresh.value);
        },
      }).catch(() => null);

      if (snapshot) {
        setLiveNow(snapshot.value);
      }

      setIsLiveLoading(false);
    },
    [owner]
  );

  React.useEffect(() => {
    if (!owner) return;

    return subscribeLiveNow(owner, (snapshot) => {
      setLiveNow(snapshot.value);
    });
  }, [owner]);

  React.useEffect(() => {
    if (!open || !owner) return;

    const tick = () => {
      if (document.hidden) return;
      void loadLiveNow();
    };

    const release = acquireJanusSweep();
    const timer = window.setInterval(tick, LIVE_REFRESH_MS);

    return () => {
      window.clearInterval(timer);
      release();
    };
  }, [open, owner, loadLiveNow]);

  React.useEffect(() => {
    if (!open) return;

    const onVisible = () => {
      if (document.hidden) return;
      void loadLiveNow();
    };

    document.addEventListener("visibilitychange", onVisible);

    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [open, loadLiveNow]);

  React.useEffect(() => {
    if (!open) return;
    void loadLiveNow();
  }, [open, loadLiveNow]);

  React.useEffect(() => {
    if (!open) return;

    const timer = window.setInterval(() => {
      if (document.hidden) return;
      void loadLiveNow();
    }, LIVE_REFRESH_MS);

    return () => window.clearInterval(timer);
  }, [open, loadLiveNow]);




  const ownerRef = React.useRef<string | null>(null);

  React.useEffect(() => {
    if (!owner) return;

    const previous = ownerRef.current;
    ownerRef.current = owner;

    if (previous && previous !== owner) {
      dropGlobalSearchCache();
      dropLiveRoomsCache();
      dropResolveCache();
      forgetWarmup();
      dropLiveNowStore();
      dropListState();
      sessionCacheRef.current.clear();
      forgetScopedData();
      setQuery("");
      setSubmitted("");
      setScope("all");
      setBrowse({ kind: "none" });
      setPreview(null);
      setRecent([]);
      setSessionLookup({ status: "idle" });
      return;
    }

    if (hydrated) return;

    const saved = readState(owner);

    if (saved) {
      setQuery(saved.query);
      setSubmitted(saved.query);
      setScope(saved.scope);
      setBrowse(saved.browse);
      setPreview(saved.preview);
    }

    setRecent(readRecent(owner));
    setHydrated(true);
  }, [owner, hydrated]);

  React.useEffect(() => {
    if (!open || !owner) return;
    setRecent(readRecent(owner));
  }, [open, owner]);

  React.useEffect(() => {
    if (!hydrated || !owner) return;
    writeState({ owner, query, scope, browse, preview });
  }, [hydrated, owner, query, scope, browse, preview]);

  const scopeKey = owner && scope !== "all" ? `${owner}:scope:${scope}` : null;
  const scopeLoadedRef = React.useRef("");
  const scopeRestoreRef = React.useRef<{ key: string; page: number } | null>(null);

  React.useEffect(() => {
    if (!scopeKey) return;

    const saved = readList<{ id: number }>(scopeKey);

    if (!saved) {
      scopeRestoreRef.current = { key: scopeKey, page: 1 };
      scopeSeenRef.current = new Set();
      scopeLoadedRef.current = "";
      setScopePage(1);
      setScopeHasMore(false);
      return;
    }

    scopeRestoreRef.current = { key: scopeKey, page: saved.page };

    scopeSeenRef.current = new Set(saved.seen);
    scopeLoadedRef.current = `${scopeKey}:${saved.page}`;
    setIsScopeLoading(false);
    setIsAppending(false);

    if (scope === "students") setScopeStudents(saved.items as StudentHit[]);
    if (scope === "assignments") setScopeAssignments(saved.items as AssignmentHit[]);
    if (scope === "groups") setScopeGroups(saved.items as GroupHit[]);

    setScopeHasMore(saved.hasMore);
    setScopePage(saved.page);

    if (Date.now() - saved.at < SCOPE_STALE_MS) return;

    let cancelled = false;

    const merge = <T extends { id: number }>(
      page: Page<T>,
      setter: React.Dispatch<React.SetStateAction<T[]>>
    ) => {
      if (cancelled || page.items.length === 0) return;

      const known = new Set(page.items.map((item) => item.id));

      setter((previous) => [...page.items, ...previous.filter((item) => !known.has(item.id))]);
    };

    const revalidate = async () => {
      if (scope === "students") {
        merge(await fetchAllStudents(1).catch(() => EMPTY_PAGE<StudentHit>()), setScopeStudents);
      } else if (scope === "assignments") {
        merge(
          await fetchRecentAssignments(1).catch(() => EMPTY_PAGE<AssignmentHit>()),
          setScopeAssignments
        );
      } else {
        merge(await fetchAllGroups(1).catch(() => EMPTY_PAGE<GroupHit>()), setScopeGroups);
      }
    };

    void revalidate();

    return () => {
      cancelled = true;
    };
  }, [scope, scopeKey]);

  React.useEffect(() => {
    if (!scopeKey) return;

    const items =
      scope === "students" ? scopeStudents : scope === "assignments" ? scopeAssignments : scopeGroups;

    if (items.length === 0) return;

    writeList(scopeKey, {
      items,
      page: scopePage,
      hasMore: scopeHasMore,
      seen: [...scopeSeenRef.current],
    });
  }, [scopeKey, scope, scopeStudents, scopeAssignments, scopeGroups, scopePage, scopeHasMore]);

  React.useEffect(() => {
    const items =
      scope === "students" ? scopeStudents : scope === "assignments" ? scopeAssignments : scopeGroups;

    scopeSeenRef.current = new Set(items.map((item) => item.id));
  }, [scope, scopeStudents, scopeAssignments, scopeGroups]);

  React.useEffect(() => {
    if (!scopeKey || term.length >= 2) return;
    if (browse.kind !== "none") return;

    const restore = scopeRestoreRef.current;

    if (restore && restore.key === scopeKey) {
      if (restore.page !== scopePage) {
        setIsScopeLoading(false);
        setIsAppending(false);
        return;
      }

      scopeRestoreRef.current = null;
    }

    const marker = `${scopeKey}:${scopePage}`;

    if (scopeLoadedRef.current === marker) {
      setIsScopeLoading(false);
      setIsAppending(false);
      return;
    }

    let cancelled = false;
    setIsScopeLoading(scopePage === 1);
    setIsAppending(scopePage > 1);

    const apply = <T extends { id: number }>(
      page: Page<T>,
      setter: React.Dispatch<React.SetStateAction<T[]>>
    ) => {
      const fresh = takeFresh(page.items, scopeSeenRef.current);

      setter((previous) => {
        if (scopePage === 1) return page.items;

        const known = new Set(previous.map((item) => item.id));

        return [...previous, ...page.items.filter((item) => !known.has(item.id))];
      });

      setScopeHasMore(page.hasMore && (scopePage === 1 || fresh.length > 0));
      scopeLoadedRef.current = marker;
    };

    const load = async () => {
      if (scope === "students") {
        const page = await fetchAllStudents(scopePage).catch(() => EMPTY_PAGE<StudentHit>());
        if (!cancelled) apply(page, setScopeStudents);
      } else if (scope === "assignments") {
        const page = await fetchRecentAssignments(scopePage).catch(() => EMPTY_PAGE<AssignmentHit>());
        if (!cancelled) apply(page, setScopeAssignments);
      } else {
        const page = await fetchAllGroups(scopePage).catch(() => EMPTY_PAGE<GroupHit>());
        if (!cancelled) apply(page, setScopeGroups);
      }

      if (!cancelled) {
        setIsScopeLoading(false);
        setIsAppending(false);
      }
    };

    void load();

    return () => {
      cancelled = true;
    };
  }, [scope, scopeKey, term, browse.kind, scopePage]);

  const showPreview =
    browse.kind !== "live" &&
    !(browse.kind === "none" && scope === "assignments" && term.length < 2);

  React.useEffect(() => {
    if (!showPreview) setPreview(null);
  }, [showPreview]);

  const loadMoreScope = React.useCallback(() => {
    if (isAppending || isScopeLoading || !scopeHasMore) return;
    setScopePage((page) => page + 1);
  }, [isAppending, isScopeLoading, scopeHasMore]);

  const loadMoreBrowse = React.useCallback(() => {
    if (isAppending || isBrowseLoading || !browseHasMore) return;
    setBrowsePage((page) => page + 1);
  }, [isAppending, isBrowseLoading, browseHasMore]);

  const browseKind = browse.kind;
  const browseGroupId = browse.kind === "group" ? browse.group.id : null;
  const browseAssignmentId = browse.kind === "exam" ? browse.assignment.id : null;
  const browseByViolations = browse.kind === "exam" ? browse.byViolations : false;

  const previewAssignmentId =
    preview?.kind === "assignment"
      ? preview.assignment.id
      : preview?.kind === "participant"
      ? preview.assignment.id
      : null;

  const { data: browseTrustList } = useGetTrustScoresByAssignmentQuery(
    browseAssignmentId != null ? { assignmentId: browseAssignmentId } : skipToken
  );

  const { data: previewTrustList } = useGetTrustScoresByAssignmentQuery(
    previewAssignmentId != null ? { assignmentId: previewAssignmentId } : skipToken
  );

  const browseTrustScores = React.useMemo(() => toTrustMap(browseTrustList), [browseTrustList]);
  const previewTrustScores = React.useMemo(() => toTrustMap(previewTrustList), [previewTrustList]);


  const browseKey =
    owner && browseKind !== "none"
      ? `${owner}:browse:${browseKind}:${browseGroupId ?? browseAssignmentId ?? 0}:${browseByViolations ? 1 : 0}`
      : null;
  const browseLoadedRef = React.useRef("");
  const browseRestoreRef = React.useRef<{ key: string; page: number } | null>(null);

  React.useEffect(() => {
    if (!browseKey) return;

    const saved = readList<{ id: number }>(browseKey);

    if (!saved) {
      browseRestoreRef.current = { key: browseKey, page: 1 };
      browseSeenRef.current = new Set();
      browseLoadedRef.current = "";
      setBrowsePage(1);
      setBrowseHasMore(false);
      return;
    }

    browseRestoreRef.current = { key: browseKey, page: saved.page };

    browseSeenRef.current = new Set(saved.seen);
    browseLoadedRef.current = `${browseKey}:${saved.page}`;
    setIsBrowseLoading(false);
    setIsAppending(false);

    if (ASSIGNMENT_BROWSE_KINDS.has(browseKind)) setBrowseAssignments(saved.items as AssignmentHit[]);
    else setBrowseStudents(saved.items as StudentHit[]);

    setBrowseHasMore(saved.hasMore);
    setBrowsePage(saved.page);
  }, [browseKey, browseKind]);

  React.useEffect(() => {
    if (!browseKey) return;

    const items: Array<AssignmentHit | StudentHit> = ASSIGNMENT_BROWSE_KINDS.has(browseKind)
      ? browseAssignments
      : browseStudents;

    if (items.length === 0) return;

    writeList<AssignmentHit | StudentHit>(browseKey, {
      items,
      page: browsePage,
      hasMore: browseHasMore,
      seen: [...browseSeenRef.current],
    });
  }, [browseKey, browseKind, browseAssignments, browseStudents, browsePage, browseHasMore]);

  React.useEffect(() => {
    const items: Array<{ id: number }> = ASSIGNMENT_BROWSE_KINDS.has(browseKind)
      ? browseAssignments
      : browseStudents;

    browseSeenRef.current = new Set(items.map((item) => item.id));
  }, [browseKind, browseAssignments, browseStudents]);

  React.useEffect(() => {
    if (!browseKey) return;

    const restore = browseRestoreRef.current;

    if (restore && restore.key === browseKey) {
      if (restore.page !== browsePage) {
        setIsBrowseLoading(false);
        setIsAppending(false);
        return;
      }

      browseRestoreRef.current = null;
    }

    const marker = `${browseKey}:${browsePage}`;

    if (browseLoadedRef.current === marker) {
      setIsBrowseLoading(false);
      setIsAppending(false);
      return;
    }

    let cancelled = false;
    setIsBrowseLoading(browsePage === 1);
    setIsAppending(browsePage > 1);

    const apply = <T extends { id: number }>(
      page: Page<T>,
      setter: React.Dispatch<React.SetStateAction<T[]>>
    ) => {
      const fresh = takeFresh(page.items, browseSeenRef.current);

      setter((previous) => {
        if (browsePage === 1) return page.items;

        const known = new Set(previous.map((item) => item.id));

        return [...previous, ...page.items.filter((item) => !known.has(item.id))];
      });

      setBrowseHasMore(page.hasMore && (browsePage === 1 || fresh.length > 0));
      browseLoadedRef.current = marker;
    };

    const load = async () => {
      if (ASSIGNMENT_BROWSE_KINDS.has(browseKind)) {
        const page = await (browseKind === "suspended"
          ? fetchAssignmentsByStatus("suspended", browsePage)
          : fetchRunningProctoredAssignments(browsePage)
        ).catch(() => EMPTY_PAGE<AssignmentHit>());

        if (!cancelled) apply(page, setBrowseAssignments);

        const missing = page.items.filter((assignment) => assignment.studentsCount == null);

        if (missing.length > 0) {
          const counts = await Promise.all(
            missing.map(async (assignment) => ({
              id: assignment.id,
              total: await fetchAssignmentParticipantsCount(assignment.id).catch(() => null),
            }))
          );

          if (!cancelled) {
            setParticipantCounts((prev) => {
              const next = { ...prev };
              for (const item of counts) {
                if (typeof item.total === "number") next[item.id] = item.total;
              }
              return next;
            });
          }
        }
      } else if (browseKind === "all-students") {
        const page = await fetchAllStudents(browsePage).catch(() => EMPTY_PAGE<StudentHit>());
        if (!cancelled) apply(page, setBrowseStudents);
      } else if (browseKind === "group" && browseGroupId != null) {
        const page = await fetchGroupStudents(browseGroupId, browsePage).catch(() =>
          EMPTY_PAGE<StudentHit>()
        );
        if (!cancelled) apply(page, setBrowseStudents);
      } else if (browseAssignmentId != null) {
        const page = await fetchAssignmentStudents(browseAssignmentId, {
          byViolations: browseByViolations,
          page: browsePage,
        }).catch(() => EMPTY_PAGE<StudentHit>());

        if (!cancelled) apply(page, setBrowseStudents);
      }

      if (!cancelled) {
        setIsBrowseLoading(false);
        setIsAppending(false);
      }
    };

    void load();

    return () => {
      cancelled = true;
    };
  }, [browseKey, browseKind, browseGroupId, browseAssignmentId, browseByViolations, browsePage]);

  const [forcedSession, setForcedSession] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!open) return;

    const value = term;

    const looksLikeSession =
      isSessionNumber(value) &&
      (forcedSession === value || value.length >= SESSION_LOOKUP_MIN_DIGITS);

    if (value.length < 2 || looksLikeSession) {
      setStudents([]);
      setContextStudents([]);
      setAssignments([]);
      setGroups([]);
      setUsers([]);
      setIsSearching(false);
      return;
    }

    let cancelled = false;
    setIsSearching(true);

    const timer = setTimeout(async () => {
      const [contextResult, studentResult, assignmentResult, groupResult, userResult] =
        await Promise.allSettled([
          assignmentContextId
            ? searchStudents(value, { assignmentId: assignmentContextId, limit: 4 })
            : Promise.resolve([]),
          searchStudents(value),
          searchAssignments(value),
          searchGroups(value),
          searchUsers(value),
        ]);

      if (cancelled) return;

      const contextHits = contextResult.status === "fulfilled" ? contextResult.value : [];
      const contextIds = new Set(contextHits.map((student) => student.id));

      setContextStudents(contextHits);
      setStudents(
        (studentResult.status === "fulfilled" ? studentResult.value : []).filter(
          (student) => !contextIds.has(student.id)
        )
      );
      setAssignments(assignmentResult.status === "fulfilled" ? assignmentResult.value : []);
      setGroups(groupResult.status === "fulfilled" ? groupResult.value : []);

      const studentIds = new Set(
        [
          ...contextHits,
          ...(studentResult.status === "fulfilled" ? studentResult.value : []),
        ].map((student) => student.id)
      );

      setUsers(
        (userResult.status === "fulfilled" ? userResult.value : []).filter(
          (user) => !(user.studentId != null && studentIds.has(user.studentId))
        )
      );

      setIsSearching(false);
    }, 0);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [open, term, assignmentContextId, forcedSession]);


  React.useEffect(() => {
    if (!open || !handoff) return;

    setForcedSession(handoff.session ? handoff.query.trim() : null);

    setScope("all");
    setBrowse({ kind: "none" });
    setPreview(null);
    setQuery(handoff.query);
    setSubmitted(handoff.query);

    onHandoffConsumed?.();
  }, [open, handoff, onHandoffConsumed]);

  React.useEffect(() => {
    if (!open) return;

    const value = term;

    const forced = forcedSession === value;

    if (!isSessionNumber(value) || (!forced && value.length < SESSION_LOOKUP_MIN_DIGITS)) {
      setSessionLookup({ status: "idle" });
      return;
    }

    const attemptId = Number(value);
    const cached = sessionCacheRef.current.get(attemptId);

    if (cached) {
      setSessionLookup(cached);
      return;
    }

    let cancelled = false;

    const timer = setTimeout(async () => {
      setSessionLookup({ status: "scanning" });

      const result = await resolveByAttemptId(attemptId);

      if (cancelled) return;

      const target =
        result.kind === "resolved"
          ? result.target
          : result.kind === "choice"
          ? result.targets[0]
          : null;

      if (!target) {
        const missing: SessionLookup = { status: "not-found" };
        sessionCacheRef.current.set(attemptId, missing);
        setSessionLookup(missing);
        return;
      }

      const card = await lookupStudent(target.studentId).catch(() => null);

      const cachedStudent = (card?.hit as StudentHit | undefined) ?? null;

      if (cancelled) return;

      if (cachedStudent || target.studentName) {
        const quick: SessionLookup = { status: "found", target, student: cachedStudent };
        sessionCacheRef.current.set(attemptId, quick);
        setSessionLookup(quick);
        setPreview({
          kind: "session",
          student: cachedStudent,
          studentId: target.studentId,
          assignmentId: target.assignmentId,
          assignmentName: target.assignmentName ?? null,
          attemptId: target.attemptId ?? attemptId,
          isRunning: target.isRunning,
        });

        if (cachedStudent && Date.now() - (card?.at ?? 0) < STUDENT_CARD_FRESH_MS) return;
      }

      const student = await fetchStudentById(target.studentId, target.assignmentId).catch(
        () => null
      );

      if (cancelled) return;

      const found: SessionLookup = { status: "found", target, student };
      sessionCacheRef.current.set(attemptId, found);
      setSessionLookup(found);
      setPreview({
        kind: "session",
        student,
        studentId: target.studentId,
        assignmentId: target.assignmentId,
        assignmentName: target.assignmentName ?? null,
        attemptId: target.attemptId ?? attemptId,
        isRunning: target.isRunning,
      });
    }, 0);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [open, term, forcedSession]);

  const previewKey = React.useMemo(() => {
    if (!preview) return "none";
    if (preview.kind === "student") return `student:${preview.student.id}`;
    if (preview.kind === "participant")
      return `participant:${preview.assignment.id}:${preview.student.id}`;
    if (preview.kind === "assignment") return `assignment:${preview.assignment.id}`;
    if (preview.kind === "group") return `group:${preview.group.id}`;
    return `session:${preview.assignmentId}:${preview.attemptId ?? 0}`;
  }, [preview]);

  const previewRef = React.useRef<PreviewSelection>(preview);

  React.useEffect(() => {
    previewRef.current = preview;
  }, [preview]);

  React.useEffect(() => {
    const preview = previewRef.current;

    if (!preview) {
      setPreviewAssignments([]);
      setPreviewAttempts([]);
      setPreviewStudents([]);
      setPreviewCount(null);
      setPreviewViolations([]);
      setPreviewViolationsTotal(0);
      setPreviewLive(null);
      setPreviewPresence({});
      return;
    }

    let cancelled = false;
    setIsPreviewLoading(true);
    setPreviewLive(null);
    setPreviewPresence({});

    const load = async () => {
      if (preview.kind === "participant") {
        setPreviewAssignments([]);
        setPreviewStudents([]);
        setPreviewCount(null);
        setPreviewViolations([]);
        setPreviewViolationsTotal(0);

        const [attempts, live] = await Promise.all([
          fetchAttemptList(preview.assignment.id, preview.student.id).catch(() => []),
          fetchPresence(
            preview.assignment.id,
            preview.student.id,
            preview.student.userId,
            true
          ).catch(() => null),
        ]);

        if (cancelled) return;

        setPreviewAttempts(attempts);
        setPreviewPresence(live ? { [preview.assignment.id]: live } : {});
        setIsPreviewLoading(false);
        return;
      }

      setPreviewAttempts([]);

      if (preview.kind === "student") {
        const list = await fetchStudentAssignments(preview.student.id).catch(() => []);
        if (cancelled) return;
        setPreviewAssignments(list);

        const openExams = list
          .filter((item) => item.status === "process" && item.isProctoring)
          .slice(0, PRESENCE_EXAM_LIMIT);

        if (openExams.length === 0) {
          setPreviewLive(OFFLINE_STATUS);
          setIsPreviewLoading(false);
          return;
        }

        const statuses = await Promise.all(
          openExams.map((exam) =>
            fetchPresence(exam.id, preview.student.id, preview.student.userId, true).catch(
              () => null
            )
          )
        );

        if (cancelled) return;

        const presence: Record<number, LiveStatus> = {};

        openExams.forEach((exam, index) => {
          const status = statuses[index];
          if (status) presence[exam.id] = status;
        });

        setPreviewPresence(presence);
        setPreviewLive(
          statuses.find((status) => status?.state === "live") ??
            statuses.find((status) => status?.state === "in-exam") ??
            statuses.find(Boolean) ??
            OFFLINE_STATUS
        );
      }

      if (preview.kind === "assignment") {
        const [students, count] = await Promise.all([
          fetchAssignmentStudents(preview.assignment.id, { byViolations: true, limit: 8 }).catch(
            () => EMPTY_PAGE<StudentHit>()
          ),
          fetchAssignmentParticipantsCount(preview.assignment.id).catch(() => null),
        ]);

        if (!cancelled) {
          setPreviewStudents(students.items);
          setPreviewCount(count);
        }
      }

      if (preview.kind === "group") {
        const page = await fetchGroupStudents(preview.group.id, 1, 12).catch(() =>
          EMPTY_PAGE<StudentHit>()
        );
        if (!cancelled) setPreviewStudents(page.items);
      }

      if (preview.kind === "session") {
        const studentId = preview.studentId ?? preview.student?.id ?? null;

        const [result, live, assignments] = await Promise.all([
          preview.attemptId != null && studentId != null
            ? fetchAttemptViolations(preview.assignmentId, studentId, preview.attemptId).catch(
                () => ({ items: [], total: 0 })
              )
            : Promise.resolve({ items: [], total: 0 }),
          studentId != null
            ? fetchPresence(
                preview.assignmentId,
                studentId,
                preview.student?.userId ?? null,
                preview.isRunning
              ).catch(() => null)
            : Promise.resolve(OFFLINE_STATUS),
          studentId != null
            ? fetchStudentAssignments(studentId).catch(() => [])
            : Promise.resolve([]),
        ]);

        if (!cancelled) {
          setPreviewViolations(result.items);
          setPreviewViolationsTotal(result.total);
          setPreviewLive(live);
          setPreviewAssignments(assignments);
          setPreviewPresence(live ? { [preview.assignmentId]: live } : {});
        }
      }

      if (!cancelled) setIsPreviewLoading(false);
    };

    void load();

    return () => {
      cancelled = true;
    };
  }, [previewKey]);

  const close = () => {
    onOpenChange(false);
  };

  const runSearch = () => {
    setSubmitted(query.trim());
  };

  const resetSearch = () => {
    setQuery("");
    setSubmitted("");
    setScope("all");
    setBrowse({ kind: "none" });
    setPreview(null);
    setSessionLookup({ status: "idle" });
  };

  const goTo = (url: string, entry: RecentEntry) => {
    writeRecent(owner, entry);
    close();
    router.push(url);
  };

  const openSession = () => {
    if (sessionLookup.status !== "found") return;

    const url = buildSessionUrl(sessionLookup.target, {
      live: previewLive?.state === "live",
    });
    goTo(url, {
      label:
        sessionLookup.student?.name ??
        sessionLookup.target.studentName ??
        t("label-global-search-session", { number: sessionLookup.target.attemptId ?? "" }),
      detail: sessionLookup.target.assignmentName ?? null,
      url,
      photo: sessionLookup.student?.photo ?? null,
    });
  };

  const copyAttemptNumber = (attemptId: number) => {
    if (typeof navigator === "undefined") return;

    void navigator.clipboard
      .writeText(String(attemptId))
      .then(() => toast.success(t("label-global-search-session-copied")))
      .catch(() => toast.error(t("label-global-search-link-copy-failed")));
  };

  const selectStudent = (student: StudentHit) => {
    setPreview({ kind: "student", student });
  };

  const copyTicketLink = () => {
    if (typeof window === "undefined") return;

    const origin = window.location.origin;
    const link =
      preview?.kind === "session" && preview.attemptId != null
        ? `${origin}/${preview.attemptId}/realtime`
        : preview?.kind === "student"
        ? `${origin}${
            previewAssignments.find((item) => item.status === "process" && item.isProctoring)
              ? `/assignments/${
                  previewAssignments.find((item) => item.status === "process" && item.isProctoring)!
                    .id
                }?student=${preview.student.id}&tab=proctorings`
              : ""
          }`
        : preview?.kind === "assignment"
        ? `${origin}/assignments/${preview.assignment.id}`
        : preview?.kind === "group"
        ? `${origin}/groups/${preview.group.id}`
        : origin;

    void navigator.clipboard
      .writeText(link)
      .then(() => toast.success(t("label-global-search-link-copied")))
      .catch(() => toast.error(t("label-global-search-link-copy-failed")));
  };

  const handleCall = (student: StudentHit) => {
    if (student.userId == null) {
      toast.error(t("label-global-search-call-failed"));
      return;
    }

    void callUser({
      id: student.userId,
      firstname: student.name.split(" ")[0] ?? null,
      lastname: student.name.split(" ").slice(1).join(" ") || null,
      photo: student.photo,
      color: null,
    }).catch(() => toast.error(t("label-global-search-call-failed")));

    onOpenChange(false);
  };

  const openLiveEntry = async (item: LiveNowEntry) => {
    const exam = await resolveLiveExam(item).catch(() => null);

    if (!exam) {
      toast.error(t("label-global-search-live-exam-unknown"));
      return;
    }

    const url = `/assignments/${exam.id}?student=${item.student.id}&tab=proctorings`;

    goTo(url, {
      label: item.student.name,
      detail: exam.name || item.assignmentName || null,
      url,
      photo: item.student.photo,
    });
  };

  const openStudentAssignment = (
    student: StudentHit,
    assignment: AssignmentHit,
    forcedTab?: "students" | "proctorings"
  ) => {
    const tab =
      forcedTab ??
      (assignment.status === "process" && assignment.isProctoring ? "proctorings" : "students");
    const url = `/assignments/${assignment.id}?student=${student.id}&tab=${tab}`;

    goTo(url, { label: student.name, detail: assignment.name, url, photo: student.photo });
  };

  const liveCount = liveNow?.entries.length ?? 0;

  const lastSession = React.useMemo(
    () => recent.find((entry) => entry.url.includes("tab=proctorings")) ?? null,
    [recent]
  );

  const [liveAssignments, setLiveAssignments] = React.useState<AssignmentHit[]>([]);

  const liveByExamTarget = React.useMemo(() => {
    const map: Record<number, number> = {};

    for (const item of liveNow?.entries ?? []) {
      if (item.assignmentId == null) continue;
      map[item.assignmentId] = (map[item.assignmentId] ?? 0) + 1;
    }

    return map;
  }, [liveNow]);

  const liveByExam = useStaggeredCounts(liveByExamTarget);

  const [examLiveIds, setExamLiveIds] = React.useState<Set<number> | null>(null);

  React.useEffect(() => {
    if (browseKind !== "exam" || browseAssignmentId == null) {
      setExamLiveIds(null);
      return;
    }

    let cancelled = false;

    const peek = () => {
      void sweepAssignment(browseAssignmentId)
        .then((ids) => {
          if (!cancelled) setExamLiveIds(ids);
        })
        .catch(() => {
          if (!cancelled) setExamLiveIds(null);
        });
    };

    peek();

    const timer = window.setInterval(() => {
      if (document.hidden) return;
      peek();
    }, LIVE_REFRESH_MS);

    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [browseKind, browseAssignmentId]);

  const liveExamIds = React.useMemo(
    () => [...new Set((liveNow?.entries ?? []).map((item) => item.assignmentId))]
      .filter((id): id is number => id != null),
    [liveNow]
  );

  React.useEffect(() => {
    if (liveExamIds.length === 0) {
      setLiveAssignments([]);
      return;
    }

    let cancelled = false;

    void fetchAssignmentsByIds(liveExamIds)
      .then(async (items) => {
        if (cancelled) return;

        setLiveAssignments(items);

        const missing = items.filter((item) => item.studentsCount == null);
        if (missing.length === 0) return;

        const counts = await Promise.all(
          missing.map(async (item) => ({
            id: item.id,
            total: await fetchAssignmentParticipantsCount(item.id).catch(() => null),
          }))
        );

        if (cancelled) return;

        setParticipantCounts((previous) => {
          const next = { ...previous };

          for (const entry of counts) {
            if (entry.total != null) next[entry.id] = entry.total;
          }

          return next;
        });
      })
      .catch(() => {
        if (!cancelled) setLiveAssignments([]);
      });

    return () => {
      cancelled = true;
    };
  }, [liveExamIds]);

  const withLiveExams = React.useCallback(
    (items: AssignmentHit[]) => {
      if (liveAssignments.length === 0) return items;

      const known = new Set(items.map((item) => item.id));
      const missing = liveAssignments.filter(
        (item) => !known.has(item.id) && liveByExam[item.id] != null
      );

      return missing.length > 0 ? [...missing, ...items] : items;
    },
    [liveAssignments, liveByExam]
  );

  const renderExamIcon = (assignment: AssignmentHit) =>
    liveByExam[assignment.id] ? (
      <LivePulse size="lg" />
    ) : (
      <ClipboardList className="text-muted-foreground" />
    );

  const liveExamOf = (student: StudentHit): string | null => {
    if (examLiveIds?.has(student.id)) {
      return liveNow?.examByStudentId[student.id] ?? "";
    }

    if (!liveNow) return null;

    if (liveNow.liveStudentIds.has(student.id)) {
      return liveNow.examByStudentId[student.id] ?? "";
    }

    if (student.userId != null && liveNow.liveUserIds.has(student.userId)) {
      return liveNow.examByStudentId[student.id] ?? "";
    }

    return null;
  };

  const isLiveStudent = (student: StudentHit) => liveExamOf(student) != null;

  const withLiveStudents = (items: StudentHit[], assignmentId: number | null) => {
    const fromLive = (liveNow?.entries ?? [])
      .filter((entry) => assignmentId == null || entry.assignmentId === assignmentId)
      .map((entry) => entry.student);

    const known = new Map(items.map((student) => [student.id, student]));
    const head: StudentHit[] = [];
    const headIds = new Set<number>();

    for (const student of fromLive) {
      if (headIds.has(student.id)) continue;

      headIds.add(student.id);
      head.push(known.get(student.id) ?? student);
    }

    for (const student of items) {
      if (headIds.has(student.id) || !isLiveStudent(student)) continue;

      headIds.add(student.id);
      head.push(student);
    }

    if (head.length === 0) return items;

    return [...head, ...items.filter((student) => !headIds.has(student.id))];
  };

  const splitLiveStudents = (items: StudentHit[], assignmentId: number | null) => {
    const ordered = withLiveStudents(items, assignmentId);
    const live = ordered.filter((student) => isLiveStudent(student));

    return { live, rest: ordered.slice(live.length) };
  };

  const scopeStudentSections = splitLiveStudents(scopeStudents, null);
  const browseStudentSections = splitLiveStudents(
    browseStudents,
    browse.kind === "exam" ? browse.assignment.id : null
  );

  const presenceOf = (student: StudentHit): PresenceLevel => {
    const entry = (liveNow?.entries ?? []).find(
      (item) =>
        item.student.id === student.id ||
        (student.userId != null && item.student.userId === student.userId)
    );

    return presenceLevel(entry ?? null);
  };

  const presenceNote = (level: PresenceLevel): string | null => {
    const key = presenceLabelKey(level);
    return key ? t(key) : null;
  };

  const renderStudentRow = (student: StudentHit, prefix: string) => {
    const liveExam = liveExamOf(student);

    return (
    <CommandItem
      key={`${prefix}-${student.id}`}
      value={`${prefix}-${student.id}`}
      onSelect={() => void selectStudent(student)}
    >
      <PresenceAvatar student={student} level={presenceOf(student)} />
      <div className="flex min-w-0 flex-col">
        <span className="truncate">{student.name}</span>
        <span className="truncate text-xs text-muted-foreground">
          {(liveExam
            ? [liveExam, presenceNote(presenceOf(student))]
            : [
                formatGroups(student.groups, (count) =>
                  t("label-global-search-more-groups", { count })
                ),
                student.detail,
              ]
          )
            .filter(Boolean)
            .join(" · ") || t("label-global-search-no-group")}
        </span>
      </div>
    </CommandItem>
    );
  };

  const renderParticipantRow = (student: StudentHit, assignment: AssignmentHit) => {
    const isOpened = preview?.kind === "participant" && preview.student.id === student.id;

    return (
    <CommandItem
      key={`participant-${student.id}`}
      value={`participant-${student.id}`}
      onSelect={() => setPreview({ kind: "participant", student, assignment })}
      className={cn(
        "te-global-search__participant relative",
        isOpened &&
          "bg-primary/10 before:absolute before:inset-y-1 before:left-0 before:w-0.5 before:rounded-full before:bg-primary data-[selected=true]:bg-primary/15"
      )}
      data-opened={isOpened ? "true" : undefined}
    >
      <PresenceAvatar student={student} level={presenceOf(student)} />
      <div className="flex min-w-0 flex-col">
        <span className="truncate">{student.name}</span>
        <span className="truncate text-xs text-muted-foreground">
          {[presenceNote(presenceOf(student)), student.groups.join(", ") || student.detail || ""]
            .filter(Boolean)
            .join(" · ")}
        </span>
      </div>
      <span className="ml-auto shrink-0 [&_button]:bg-transparent">
        <ScoreCell
          points={0}
          credibility={resolveCredibility(student, browseTrustScores)}
          hasTrustScore={hasTrustScore(student, browseTrustScores)}
          compact
          hidePoints
        />
      </span>
    </CommandItem>
    );
  };

  const participantsOf = (assignment: AssignmentHit) =>
    assignment.studentsCount ?? participantCounts[assignment.id] ?? null;

  const renderRunningRow = (
    assignment: AssignmentHit,
    byViolations = false,
    from: "violations" | "suspended"
  ) => (
    <CommandItem
      key={`running-${assignment.id}`}
      value={`running-${assignment.id}`}
      onSelect={() => setBrowse({ kind: "exam", assignment, byViolations, from })}
    >
      {renderExamIcon(assignment)}
      <div className="flex min-w-0 flex-col">
        <span className="truncate">{assignment.name}</span>
        <span className="truncate text-xs text-muted-foreground">
          {[
            liveByExam[assignment.id]
              ? t("label-global-search-live-count", { count: liveByExam[assignment.id] })
              : null,
            t("label-global-search-exam-window-open"),
            typeof participantsOf(assignment) === "number"
              ? t("label-global-search-participants-count", {
                  count: participantsOf(assignment) as number,
                })
              : null,
          ]
            .filter(Boolean)
            .join(" · ")}
        </span>
      </div>
    </CommandItem>
  );

  const renderAssignmentRow = (assignment: AssignmentHit) => (
    <CommandItem
      key={`scope-assignment-${assignment.id}`}
      value={`scope-assignment-${assignment.id}`}
      onSelect={() => setBrowse({ kind: "exam", assignment, byViolations: false, from: "none" })}
    >
      {assignment.status === "process" ? renderExamIcon(assignment) : <ClipboardList />}
      <div className="flex min-w-0 flex-col">
        <span className="truncate">{assignment.name}</span>
        <span className="truncate text-xs text-muted-foreground">
          {renderAssignmentMeta(assignment)}
        </span>
      </div>
    </CommandItem>
  );

  const renderAssignmentMeta = (assignment: AssignmentHit) =>
    [
      liveByExam[assignment.id]
        ? t("label-global-search-live-count", { count: liveByExam[assignment.id] })
        : null,
      assignment.status === "process"
        ? t("label-global-search-exam-window-open")
        : t("label-realtime-finished"),
      typeof participantsOf(assignment) === "number"
        ? t("label-global-search-participants-count", { count: participantsOf(assignment) as number })
        : null,
      assignment.isProctoring ? t("label-global-search-with-proctoring") : null,
    ]
      .filter(Boolean)
      .join(" · ");

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="te-global-search flex h-[600px] max-h-[85vh] flex-col overflow-hidden p-0 sm:max-w-3xl"
        showCloseButton={false}
      >
        <VisuallyHidden>
          <DialogHeader>
            <DialogTitle>{t("label-global-search-title")}</DialogTitle>
            <DialogDescription>{t("label-global-search-placeholder")}</DialogDescription>
          </DialogHeader>
        </VisuallyHidden>

        <Command
          shouldFilter={false}
          className="flex h-full flex-col [&_[cmdk-group-heading]]:text-muted-foreground [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group]]:px-2 [&_[cmdk-input]]:h-12 [&_[cmdk-item]]:px-2 [&_[cmdk-item]]:py-3"
        >
          {browse.kind !== "none" && !selectedStudent ? (
            <div className="flex items-center gap-3 border-b px-4 py-3">
              <button
                type="button"
                onClick={() =>
                  setBrowse(
                    browse.kind === "exam" ? { kind: browse.from ?? "none" } : { kind: "none" }
                  )
                }
                className="te-global-search__back rounded-md p-1 text-muted-foreground hover:bg-muted"
                aria-label={t("btn-back")}
              >
                <ArrowLeft className="h-4 w-4" />
              </button>
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-medium">
                  {browse.kind === "violations"
                    ? t("label-global-search-violations")
                    : browse.kind === "suspended"
                    ? t("label-global-search-suspended")
                    : browse.kind === "live"
                    ? t("label-global-search-live-now")
                    : browse.kind === "all-students"
                    ? t("label-global-search-all-students")
                    : browse.kind === "group"
                    ? browse.group.name
                    : browse.assignment.name}
                </div>
                <div className="truncate text-xs text-muted-foreground">
                  {browse.kind === "violations"
                    ? t("label-global-search-violations-hint")
                    : browse.kind === "suspended"
                    ? t("label-global-search-suspended-hint")
                    : browse.kind === "live"
                    ? t("label-global-search-live-now-hint")
                    : browse.kind === "all-students"
                    ? t("label-global-search-all-students-hint")
                    : browse.kind === "group"
                    ? t("label-global-search-group-students")
                    : t("label-global-search-participants")}
                </div>
              </div>
              {browse.kind === "exam" && (
                <button
                  type="button"
                  onClick={() =>
                    setBrowse({
                      kind: "exam",
                      assignment: browse.assignment,
                      byViolations: !browse.byViolations,
                    })
                  }
                  className={cn(
                    "te-global-search__sort rounded-md border px-2 py-1 text-xs transition-colors",
                    browse.byViolations
                      ? "border-amber-300 bg-amber-50 text-amber-700"
                      : "text-muted-foreground hover:bg-muted"
                  )}
                >
                  {t("label-global-search-sort-violations")}
                </button>
              )}
            </div>
          ) : (

            <div className="flex items-center gap-2 border-b pr-3">
              <div className="min-w-0 flex-1 [&_[data-slot=command-input-wrapper]]:border-b-0">
                <CommandInput
                  value={query}
                  onValueChange={setQuery}
                  onKeyDown={(event) => {
                    if (event.key !== "Enter") return;
                    event.preventDefault();
                    runSearch();
                  }}
                  placeholder={t("label-global-search-placeholder")}
                />
              </div>

              <button
                type="button"
                onClick={runSearch}
                disabled={query.trim().length === 0 || query.trim() === term}
                className="te-global-search__submit shrink-0 rounded-md bg-foreground px-3 py-1.5 text-xs font-medium text-background disabled:opacity-40"
              >
                {t("btn-search")}
              </button>

              {(query.length > 0 || scope !== "all" || preview !== null) && (
                <button
                  type="button"
                  onClick={resetSearch}
                  className="te-global-search__reset shrink-0 rounded-md px-2 py-1 text-xs text-muted-foreground hover:bg-muted"
                >
                  {t("btn-reset")}
                </button>
              )}
            </div>
          )}

          {browse.kind === "none" && (
            <div className="flex flex-wrap gap-1 border-b px-3 py-2">
              {SCOPES.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => setScope(item.id)}
                  className={cn(
                    "te-global-search__scope relative rounded-md px-2.5 py-1 text-xs transition-colors",
                    scope === item.id
                      ? "bg-foreground text-background"
                      : "text-muted-foreground hover:bg-muted"
                  )}
                >
                  {t(item.labelKey)}
                  {liveCount > 0 && (item.id === "students" || item.id === "assignments") && (
                    <span className="absolute -right-0.5 -top-0.5">
                      <LivePulse size="sm" />
                    </span>
                  )}
                </button>
              ))}

              <span
                className={cn(
                  "te-global-search__live ml-auto inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 py-1 text-xs font-medium ring-1 ring-inset transition-colors",
                  liveCount > 0
                    ? "bg-red-50 text-red-700 ring-red-200"
                    : "text-muted-foreground ring-border"
                )}
              >
                {isLiveLoading ? (
                  <Loader2 className="h-3 w-3 animate-spin" />
                ) : liveCount > 0 ? (
                  <LivePulse size="md" />
                ) : (
                  <span className="h-1.5 w-1.5 rounded-full bg-zinc-300" />
                )}
                {t("label-global-search-live-now")}
                {liveCount > 0 && <span>{liveCount}</span>}
              </span>
            </div>
          )}

          <div
            className={cn(
              "grid min-h-0 flex-1 grid-cols-1",
              showPreview && "sm:grid-cols-[1.35fr_1fr]"
            )}
          >
          <CommandList
            className={cn(
              "relative min-h-0 max-h-none overflow-y-auto",
              showPreview && "border-r"
            )}
          >
            {browse.kind !== "none" ? (
              <>
                {isBrowseLoading && (
                  <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 text-sm text-muted-foreground">
                    <Loader2 className="h-6 w-6 animate-spin" />
                    {t("label-loading")}
                  </div>
                )}

                {browse.kind === "live" && (
                  <CommandGroup heading={t("label-global-search-live-now")}>
                    {isLiveLoading && (
                      <div className="flex min-h-[280px] flex-col items-center justify-center gap-3 px-2 py-10 text-sm text-muted-foreground">
                        <Loader2 className="h-6 w-6 animate-spin" />
                        {t("label-loading")}
                      </div>
                    )}

                    {!isLiveLoading && (liveNow?.entries.length ?? 0) === 0 && (
                      <div className="px-2 py-4 text-sm text-muted-foreground">
                        {t("label-global-search-live-empty")}
                      </div>
                    )}

                    {!isLiveLoading &&
                      liveNow?.entries.map((item) => (
                        <CommandItem
                          key={`live-${item.assignmentId}-${item.student.id}`}
                          value={`live-${item.assignmentId}-${item.student.id}`}
                          onSelect={() => void openLiveEntry(item)}
                        >
                          <PresenceAvatar student={item.student} level={presenceLevel(item)} />
                          <div className="flex min-w-0 flex-col">
                            <span className="truncate">{item.student.name}</span>
                            <span className="truncate text-xs text-muted-foreground">
                              {item.assignmentName}
                            </span>
                          </div>
                          <span className="ml-auto inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full bg-red-50 px-2.5 py-1 text-[11px] font-medium text-red-700 ring-1 ring-inset ring-red-200">
                            <LivePulse size="sm" />
                            {t("label-global-search-chip-live")}
                          </span>
                        </CommandItem>
                      ))}

                    {!isLiveLoading && liveNow && liveNow.total > liveNow.scanned && (
                      <div className="px-2 pb-2 pt-1 text-[11px] text-muted-foreground">
                        {t("label-global-search-scanned-exams", {
                          scanned: liveNow.scanned,
                          total: liveNow.total,
                        })}
                      </div>
                    )}
                  </CommandGroup>
                )}

                {!isBrowseLoading && (browse.kind === "violations" || browse.kind === "suspended") && (
                  <>
                    {browseAssignments.length === 0 && (
                      <div className="px-2 py-4 text-sm text-muted-foreground">
                        {browse.kind === "suspended"
                          ? t("label-global-search-no-suspended")
                          : t("label-global-search-no-running")}
                      </div>
                    )}
                    <CommandGroup>
                      {browseAssignments.map((assignment) =>
                        renderRunningRow(assignment, browse.kind === "violations", browse.kind)
                      )}
                      {browseHasMore && (
                        <LoadMore
                          onReach={loadMoreBrowse}
                          busy={isAppending}
                          loaded={browseStudents.length + browseAssignments.length}
                        />
                      )}
                    </CommandGroup>
                  </>
                )}

                {!isBrowseLoading && browse.kind === "all-students" && (
                  <>
                    {browseStudents.length === 0 && (
                      <div className="px-2 py-4 text-sm text-muted-foreground">
                        {t("label-global-search-no-participants")}
                      </div>
                    )}
                    {browseStudentSections.live.length > 0 && (
                      <CommandGroup heading={t("label-global-search-students-in-exam")}>
                        {browseStudentSections.live.map((student) =>
                          renderStudentRow(student, "all-student")
                        )}
                      </CommandGroup>
                    )}
                    <CommandGroup
                      heading={
                        browseStudentSections.rest.length > 0 &&
                        browseStudentSections.live.length > 0
                          ? t("label-global-search-students-idle")
                          : undefined
                      }
                    >
                      {browseStudentSections.rest.map((student) =>
                        renderStudentRow(student, "all-student")
                      )}
                      {browseHasMore && (
                        <LoadMore
                          onReach={loadMoreBrowse}
                          busy={isAppending}
                          loaded={browseStudents.length + browseAssignments.length}
                        />
                      )}
                    </CommandGroup>
                  </>
                )}

                {!isBrowseLoading && browse.kind === "group" && (
                  <>
                    {browseStudents.length === 0 && (
                      <div className="px-2 py-4 text-sm text-muted-foreground">
                        {t("label-global-search-no-participants")}
                      </div>
                    )}
                    {browseStudentSections.live.length > 0 && (
                      <CommandGroup heading={t("label-global-search-students-in-exam")}>
                        {browseStudentSections.live.map((student) =>
                          renderStudentRow(student, "group-student")
                        )}
                      </CommandGroup>
                    )}
                    <CommandGroup
                      heading={
                        browseStudentSections.rest.length > 0 &&
                        browseStudentSections.live.length > 0
                          ? t("label-global-search-students-idle")
                          : undefined
                      }
                    >
                      {browseStudentSections.rest.map((student) =>
                        renderStudentRow(student, "group-student")
                      )}
                      {browseHasMore && (
                        <LoadMore
                          onReach={loadMoreBrowse}
                          busy={isAppending}
                          loaded={browseStudents.length + browseAssignments.length}
                        />
                      )}
                    </CommandGroup>
                  </>
                )}

                {!isBrowseLoading && browse.kind === "exam" && (
                  <>
                    {browseStudents.length === 0 && (
                      <div className="px-2 py-4 text-sm text-muted-foreground">
                        {t("label-global-search-no-participants")}
                      </div>
                    )}
                    {browseStudentSections.live.length > 0 && (
                      <CommandGroup heading={t("label-global-search-students-in-exam")}>
                        {browseStudentSections.live.map((student) =>
                          renderParticipantRow(student, browse.assignment)
                        )}
                      </CommandGroup>
                    )}
                    <CommandGroup
                      heading={
                        browseStudentSections.rest.length === 0
                          ? undefined
                          : browseStudentSections.live.length > 0
                          ? t("label-global-search-students-idle")
                          : t("label-global-search-participants")
                      }
                    >
                      {browseStudentSections.rest.map((student) =>
                        renderParticipantRow(student, browse.assignment)
                      )}
                      {browseHasMore && (
                        <LoadMore
                          onReach={loadMoreBrowse}
                          busy={isAppending}
                          loaded={browseStudents.length + browseAssignments.length}
                        />
                      )}
                    </CommandGroup>
                  </>
                )}

              </>
            ) : (
              <>
                {!isSearching &&
                  sessionLookup.status === "idle" &&
                  term.length >= 2 &&
                  students.length === 0 &&
                  contextStudents.length === 0 &&
                  assignments.length === 0 &&
                  groups.length === 0 && (
                    <div className="px-2 py-6 text-center">
                      <div className="text-sm font-medium">{t("label-global-search-empty")}</div>
                      <div className="mt-1 text-xs text-muted-foreground">
                        {t("label-global-search-empty-hint")}
                      </div>
                      <div className="mt-3 flex flex-wrap justify-center gap-2">
                        <button
                          type="button"
                          onClick={() => setBrowse({ kind: "live" })}
                          className="te-global-search__empty-action rounded-md border px-3 py-1.5 text-sm hover:bg-muted"
                        >
                          {t("label-global-search-live-now")}
                        </button>
                        <button
                          type="button"
                          onClick={() => setBrowse({ kind: "all-students" })}
                          className="te-global-search__empty-action rounded-md border px-3 py-1.5 text-sm hover:bg-muted"
                        >
                          {t("label-global-search-all-students")}
                        </button>
                      </div>
                    </div>
                  )}

                {sessionLookup.status !== "idle" && (
                  <CommandGroup heading={t("label-global-search-sessions")}>
                    {sessionLookup.status === "scanning" && (
                      <div className="flex items-center gap-3 px-2 py-3 text-sm">
                        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                        <div className="flex flex-col">
                          <span>
                            {t("label-global-search-open-session", { number: term })}
                          </span>
                          <span className="text-xs text-muted-foreground">
                            {t("label-global-search-session-hint")}
                          </span>
                        </div>
                      </div>
                    )}

                    {sessionLookup.status === "not-found" && (
                      <div className="flex items-center gap-3 px-2 py-3 text-sm text-muted-foreground">
                        <SearchX className="h-5 w-5" />
                        {t("label-realtime-not-found")}
                      </div>
                    )}

                    {sessionLookup.status === "found" && (
                      <CommandItem value={`session-${term}`} onSelect={openSession}>
                        {sessionLookup.student ? (
                          <StudentAvatar student={sessionLookup.student} />
                        ) : (
                          <Radio className="text-red-600" />
                        )}
                        <div className="flex min-w-0 flex-col">
                          <span className="truncate">
                            {sessionLookup.student?.name ??
                              sessionLookup.target.studentName ??
                              t("label-global-search-session", { number: term })}
                          </span>
                          <span className="truncate text-xs text-muted-foreground">
                            {[
                              sessionLookup.student?.groups.join(", ") || null,
                              sessionLookup.target.assignmentName,
                              previewLive?.state === "live"
                                ? t("label-global-search-live-camera")
                                : previewLive?.state === "in-exam"
                                ? t("label-global-search-in-exam-no-camera")
                                : previewLive?.state === "away"
                                ? t("label-global-search-away")
                                : previewLive?.state === "finished"
                                ? t("label-global-search-attempt-finished")
                                : sessionLookup.target.isRunning
                                ? t("label-global-search-exam-window-open")
                                : t("label-realtime-finished"),
                            ]
                              .filter(Boolean)
                              .join(" · ")}
                          </span>
                        </div>
                        {previewLive?.state === "live" && (
                          <Radio className="ml-auto h-4 w-4 shrink-0 text-red-600" />
                        )}
                      </CommandItem>
                    )}
                  </CommandGroup>
                )}

                {(scope === "all" || scope === "students") && contextStudents.length > 0 && (
                  <CommandGroup heading={t("label-global-search-in-this-exam")}>
                    {contextStudents.map((student) => renderStudentRow(student, "context"))}
                  </CommandGroup>
                )}

                {(scope === "all" || scope === "students") && students.length > 0 && (
                  <CommandGroup heading={t("label-global-search-students")}>
                    {students.map((student) => renderStudentRow(student, "student"))}
                  </CommandGroup>
                )}

                {(scope === "all" || scope === "assignments") && assignments.length > 0 && (
                  <CommandGroup heading={t("label-global-search-assignments")}>
                    {assignments.map((assignment) => (
                      <CommandItem
                        key={`assignment-${assignment.id}`}
                        value={`assignment-${assignment.id}`}
                        onSelect={() => setPreview({ kind: "assignment", assignment })}
                      >
                        <ClipboardList />
                        <div className="flex min-w-0 flex-col">
                          <span className="truncate">{assignment.name}</span>
                          <span className="truncate text-xs text-muted-foreground">
                            {renderAssignmentMeta(assignment)}
                          </span>
                        </div>
                      </CommandItem>
                    ))}
                  </CommandGroup>
                )}

                {(scope === "all" || scope === "students") && users.length > 0 && (
                  <CommandGroup heading={t("label-global-search-users")}>
                    {users.map((user) => (
                      <CommandItem
                        key={`user-${user.id}`}
                        value={`user-${user.id}`}
                        onSelect={() => {
                          if (user.studentId != null) {
                            selectStudent({
                              id: user.studentId,
                              userId: user.id,
                              name: user.name,
                              detail: user.detail,
                              photo: user.photo,
                              email: null,
                              phone: null,
                              externalId: null,
                              registerDate: null,
                              lastActivityDate: null,
                              groups: [],
                            });
                            return;
                          }

                          toast.info(t("label-global-search-user-no-page"));
                        }}
                      >
                        <StudentAvatar
                          student={{
                            id: user.id,
                            userId: user.id,
                            name: user.name,
                            detail: null,
                            photo: user.photo,
                            email: null,
                            phone: null,
                            externalId: null,
                            registerDate: null,
                            lastActivityDate: null,
                            groups: [],
                          }}
                        />
                        <div className="flex min-w-0 flex-col">
                          <span className="truncate">{user.name}</span>
                          <span className="truncate text-xs text-muted-foreground">
                            {[user.role ? roleLabel(user.role) : null, user.detail]
                              .filter(Boolean)
                              .join(" · ")}
                          </span>
                        </div>
                      </CommandItem>
                    ))}
                  </CommandGroup>
                )}

                {(scope === "all" || scope === "groups") && groups.length > 0 && (
                  <CommandGroup heading={t("label-global-search-groups")}>
                    {groups.map((group) => (
                      <CommandItem
                        key={`group-${group.id}`}
                        value={`group-${group.id}`}
                        onSelect={() => setPreview({ kind: "group", group })}
                      >
                        <Users />
                        <span className="truncate">{group.name}</span>
                      </CommandItem>
                    ))}
                  </CommandGroup>
                )}

                {term.length < 2 && scope === "all" && (
                  <div className="px-3 pb-1 pt-2 text-xs text-muted-foreground">
                    {t("label-global-search-examples")}
                  </div>
                )}

                {term.length < 2 && scope !== "all" && isScopeLoading && (
                  <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 text-sm text-muted-foreground">
                    <Loader2 className="h-6 w-6 animate-spin" />
                    {t("label-loading")}
                  </div>
                )}

                {term.length < 2 && scope === "students" && !isScopeLoading && (
                  <>
                    {scopeStudentSections.live.length > 0 && (
                      <CommandGroup heading={t("label-global-search-students-in-exam")}>
                        {scopeStudentSections.live.map((student) =>
                          renderStudentRow(student, "scope-student")
                        )}
                      </CommandGroup>
                    )}
                    <CommandGroup
                      heading={
                        scopeStudentSections.rest.length === 0
                          ? undefined
                          : scopeStudentSections.live.length > 0
                          ? t("label-global-search-students-idle")
                          : t("label-global-search-all-students")
                      }
                    >
                      {scopeStudentSections.rest.map((student) =>
                        renderStudentRow(student, "scope-student")
                      )}
                      {scopeHasMore && (
                        <LoadMore
                          onReach={loadMoreScope}
                          busy={isAppending}
                          loaded={scopeStudents.length + scopeAssignments.length + scopeGroups.length}
                        />
                      )}
                    </CommandGroup>
                  </>
                )}

                {term.length < 2 && scope === "assignments" && !isScopeLoading && (
                  <>
                    {groupAssignments(withLiveExams(scopeAssignments), liveByExam).map((section) => (
                      <CommandGroup key={section.id} heading={t(section.labelKey)}>
                        {section.items.map((assignment) => renderAssignmentRow(assignment))}
                      </CommandGroup>
                    ))}
                    {scopeHasMore && (
                      <CommandGroup>
                        <LoadMore
                          onReach={loadMoreScope}
                          busy={isAppending}
                          loaded={scopeStudents.length + scopeAssignments.length + scopeGroups.length}
                        />
                      </CommandGroup>
                    )}
                  </>
                )}

                {term.length < 2 && scope === "groups" && !isScopeLoading && (
                  <CommandGroup heading={t("label-global-search-groups")}>
                    {scopeGroups.map((group) => (
                      <CommandItem
                        key={`scope-group-${group.id}`}
                        value={`scope-group-${group.id}`}
                        onSelect={() => setPreview({ kind: "group", group })}
                      >
                        <Users />
                        <span className="truncate">{group.name}</span>
                      </CommandItem>
                    ))}
                    {scopeHasMore && (
                      <LoadMore
                        onReach={loadMoreScope}
                        busy={isAppending}
                        loaded={scopeStudents.length + scopeAssignments.length + scopeGroups.length}
                      />
                    )}
                  </CommandGroup>
                )}

                {term.length < 2 && scope === "all" && liveCount > 0 && (
                  <CommandGroup
                    heading={`${t("label-global-search-live-now")} · ${liveCount}`}
                  >
                    {(liveNow?.entries ?? []).slice(0, LIVE_PREVIEW_LIMIT).map((item) => (
                      <CommandItem
                        key={`live-preview-${item.student.id}`}
                        value={`live-preview-${item.student.id}`}
                        onSelect={() => void openLiveEntry(item)}
                      >
                        <PresenceAvatar student={item.student} level={presenceLevel(item)} />
                        <div className="flex min-w-0 flex-col">
                          <span className="truncate">{item.student.name}</span>
                          <span className="truncate text-xs text-muted-foreground">
                            {[
                              item.assignmentName || t("label-global-search-live-now-hint"),
                              presenceNote(presenceLevel(item)),
                            ]
                              .filter(Boolean)
                              .join(" · ")}
                          </span>
                        </div>
                      </CommandItem>
                    ))}
                    {liveCount > LIVE_PREVIEW_LIMIT && (
                      <CommandItem
                        value="live-preview-all"
                        onSelect={() => setBrowse({ kind: "live" })}
                      >
                        <Radio className="text-red-600" />
                        <span>
                          {t("label-global-search-live-show-all", { count: liveCount })}
                        </span>
                      </CommandItem>
                    )}
                  </CommandGroup>
                )}

                {term.length < 2 && scope === "all" && (
                  <CommandGroup heading={t("label-global-search-quick")}>
                    {liveCount > 0 && (
                      <CommandItem value="browse-live" onSelect={() => setBrowse({ kind: "live" })}>
                        <span className="flex h-4 w-4 shrink-0 items-center justify-center">
                          <LivePulse size="sm" />
                        </span>
                        <div className="flex flex-col">
                          <span>
                            {t("label-global-search-live-now")} · {liveCount}
                          </span>
                          <span className="text-xs text-muted-foreground">
                            {t("label-global-search-live-now-hint")}
                          </span>
                        </div>
                      </CommandItem>
                    )}
                    <CommandItem
                      value="browse-all-students"
                      onSelect={() => setBrowse({ kind: "all-students" })}
                    >
                      <Users />
                      <div className="flex flex-col">
                        <span>{t("label-global-search-all-students")}</span>
                        <span className="text-xs text-muted-foreground">
                          {t("label-global-search-all-students-hint")}
                        </span>
                      </div>
                    </CommandItem>
                    <CommandItem value="scope-all-exams" onSelect={() => setScope("assignments")}>
                      <ClipboardList />
                      <div className="flex flex-col">
                        <span>{t("label-global-search-all-exams")}</span>
                        <span className="text-xs text-muted-foreground">
                          {t("label-global-search-all-exams-hint")}
                        </span>
                      </div>
                    </CommandItem>
                    {lastSession && (
                      <CommandItem
                        value="quick-last-session"
                        onSelect={() =>
                          goTo(lastSession.url, {
                            label: lastSession.label,
                            detail: lastSession.detail,
                            url: lastSession.url,
                            photo: lastSession.photo,
                          })
                        }
                      >
                        <History className="text-muted-foreground" />
                        <div className="flex min-w-0 flex-col">
                          <span>{t("label-global-search-last-session")}</span>
                          <span className="truncate text-xs text-muted-foreground">
                            {[lastSession.label, lastSession.detail].filter(Boolean).join(" · ")}
                          </span>
                        </div>
                      </CommandItem>
                    )}
                    <CommandItem
                      value="browse-violations"
                      onSelect={() => setBrowse({ kind: "violations" })}
                    >
                      <ShieldAlert className="text-amber-600" />
                      <div className="flex flex-col">
                        <span>{t("label-global-search-violations")}</span>
                        <span className="text-xs text-muted-foreground">
                          {t("label-global-search-violations-hint")}
                        </span>
                      </div>
                    </CommandItem>
                    <CommandItem
                      value="browse-suspended"
                      onSelect={() => setBrowse({ kind: "suspended" })}
                    >
                      <PauseCircle className="text-muted-foreground" />
                      <div className="flex flex-col">
                        <span>{t("label-global-search-suspended")}</span>
                        <span className="text-xs text-muted-foreground">
                          {t("label-global-search-suspended-hint")}
                        </span>
                      </div>
                    </CommandItem>
                    <CommandItem value="scope-all-groups" onSelect={() => setScope("groups")}>
                      <UsersRound />
                      <div className="flex flex-col">
                        <span>{t("label-global-search-all-groups")}</span>
                        <span className="text-xs text-muted-foreground">
                          {t("label-global-search-all-groups-hint")}
                        </span>
                      </div>
                    </CommandItem>
                  </CommandGroup>
                )}

                {term.length < 2 && scope === "all" && recent.length > 0 && (
                  <CommandGroup heading={t("label-global-search-recent")}>
                    {recent.map((entry) => (
                      <CommandItem
                        key={entry.url}
                        value={entry.url}
                        onSelect={() => goTo(entry.url, entry)}
                      >
                        {entry.photo ? (
                          <StudentAvatar
                            student={{
                              id: 0,
                              userId: null,
                              name: entry.label,
                              detail: null,
                              email: null,
                              phone: null,
                              externalId: null,
                              registerDate: null,
                              lastActivityDate: null,
                              photo: entry.photo,
                              groups: [],
                            }}
                          />
                        ) : (
                          <Search />
                        )}
                        <div className="flex min-w-0 flex-col">
                          <span className="truncate">{entry.label}</span>
                          {entry.detail && (
                            <span className="truncate text-xs text-muted-foreground">
                              {entry.detail}
                            </span>
                          )}
                        </div>
                      </CommandItem>
                    ))}
                  </CommandGroup>
                )}
              </>
            )}
          </CommandList>

          {showPreview && (
          <div className="hidden min-h-0 overflow-auto sm:block">
            <SearchPreview
              selection={preview}
              data={{
                attempts: previewAttempts,
                studentAssignments: previewAssignments,
                assignmentStudents: previewStudents,
                participantsCount: previewCount,
                violations: previewViolations,
                violationsTotal: previewViolationsTotal,
                live: previewLive,
                presence: previewPresence,
                trustScores: previewTrustScores,
                isLoading: isPreviewLoading,
              }}
              onOpenAssignment={(assignment, student) => {
                if (student) {
                  openStudentAssignment(
                    student,
                    assignment,
                    preview?.kind === "participant" ? "students" : undefined
                  );
                  return;
                }

                goTo(`/assignments/${assignment.id}`, {
                  label: assignment.name,
                  detail: null,
                  url: `/assignments/${assignment.id}`,
                });
              }}
              onOpenSession={openSession}
              onCopyLink={copyTicketLink}
              onCall={handleCall}
              onShowParticipants={(assignment) =>
                setBrowse({ kind: "exam", assignment, byViolations: true })
              }
              onSelectStudent={(student) => setPreview({ kind: "student", student })}
              onCopyAttempt={copyAttemptNumber}
              onWatchLive={(assignment, student) =>
                openStudentAssignment(student, assignment, "proctorings")
              }
            />
          </div>
          )}
          </div>
        </Command>
      </DialogContent>
    </Dialog>
  );
}

export default GlobalSearchDialog;
